import { API } from "aws-amplify";
import { GRAPHQL_AUTH_MODE } from "@aws-amplify/api";
import { GraphQLResult } from "@aws-amplify/api-graphql";
import { toast } from "react-toastify";
import {
  cancelRehabBookingQueryFn,
  createRehabBookingQueryFn,
  createRehabSlotCommentQueryFn,
  getActiveRehabBookingsByMemberQueryFn,
  getRehabSlotsByAdminQueryFn,
  getRehabSlotsQueryFn,
  getRehabSlotsWeeklyQueryFn,
  changeRehabSlotBookabilityQueryFn,
} from "./rehabQueries";
import { IRehabBooking, IRehabBookingInput } from "../../types/api";

export const getRehabSlotsByAdmin = async (date: string) => {
  const { query, inputs, variables } = getRehabSlotsByAdminQueryFn(date);
  const myQuery = `
         query getRehabSlotsByAdmin(${inputs}) {
              ${query}
         }
    `;
  try {
    const getRehabSlotsByAdminResponse = (await API.graphql({
      query: myQuery,
      variables: variables,
      authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
    })) as GraphQLResult<any>;

    // The operation is named getRehabSlotsByAdmin but the field it selects is
    // getRehabSlotsAdmin — reading the operation name gave undefined, and the
    // `in` check below then threw before anything else could happen. Every
    // other caller of this query reads the field, not the alias.
    const response = getRehabSlotsByAdminResponse.data.getRehabSlotsAdmin;
    if (!response || "message" in response) {
      const message = response?.message ?? "Kunde inte hämta rehab-tider";
      toast.error(message);
      throw new Error(message);
    }
    return response;
  } catch (error: any) {
    // Guarded: a TypeError from this function has no .errors, and reading it
    // unguarded threw a second time inside the catch.
    const message = error?.errors?.[0]?.message;
    if (message) toast.error(message);
    return false;
  }
};

export const getRehabSlots = async (date: string, member_id: string) => {
  const { query, inputs, variables } = getRehabSlotsQueryFn(date, member_id);
  const myQuery = `
         query getRehabSlots(${inputs}) {
              ${query}
         }
    `;
  try {
    const getRehabSlotsResponse = (await API.graphql({
      query: myQuery,
      variables: variables,
      authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
    })) as GraphQLResult<any>;

    const response = getRehabSlotsResponse.data.getRehabSlots;
    if ("message" in response) {
      toast.error(response.message);
      throw new Error(response.message);
    }
    return response;
  } catch (error: any) {
    toast.error(error.errors[0].message);
    return false;
  }
};

export const createRehabBooking = async (
  bookingToCreate: IRehabBookingInput,
  sendEmail: boolean,
  isAdmin: boolean
) => {
  const { query, inputs, variables } = createRehabBookingQueryFn(
    bookingToCreate,
    sendEmail,
    isAdmin
  );
  const myQuery = `
            mutation createRehabBooking(${inputs}) {
                ${query}
            }
        `;
  try {
    const getCreateRehabBookingResponse = (await API.graphql({
      query: myQuery,
      variables: variables,
      authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
    })) as GraphQLResult<any>;

    const response = getCreateRehabBookingResponse.data.createRehabBooking;
    if ("message" in response) {
      toast.error(response.message);
      throw new Error(response.message);
    }
    return response;
  } catch (error: any) {
    toast.error(error.errors[0].message);
    return false;
  }
};

export const cancelRehabBooking = async (
  input: IRehabBooking,
  sendEmail: boolean,
  isAdmin: boolean
) => {
  const { query, inputs, variables } = cancelRehabBookingQueryFn(
    input,
    sendEmail,
    isAdmin
  );
  const myQuery = `
        mutation cancelRehabBooking(${inputs}) {
            ${query}
        }
    `;
  try {
    const getCancelRehabBookingResponse = (await API.graphql({
      query: myQuery,
      variables: variables,
      authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
    })) as GraphQLResult<any>;

    const response = getCancelRehabBookingResponse.data.cancelRehabBooking;
    if ("message" in response) {
      toast.error(response.message);
      throw new Error(response.message);
    }
    return response;
  } catch (error: any) {
    toast.error(error.errors[0].message);
    return false;
  }
};

export const getActiveRehabBookingsByMember = async (
  member_id: string,
  active: boolean
) => {
  const { query, inputs, variables } = getActiveRehabBookingsByMemberQueryFn(
    member_id,
    active
  );
  const myQuery = `
      query getActiveRehabBookingsByMember(${inputs}) {
          ${query}
      }
  `;
  try {
    const getActiveRehabBookingsResponse = (await API.graphql({
      query: myQuery,
      variables: variables,
      authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
    })) as GraphQLResult<any>;

    const response =
      getActiveRehabBookingsResponse.data.getActiveRehabBookingsByMember;
    if ("message" in response) {
      toast.error(response.message);
      return response;
    }
    return response;
  } catch (error: any) {
    toast.error(error.errors[0].message);
    return error;
  }
};

export const getRehabSlotsWeekly = async (date: string) => {
  const dateString = date.slice(0, 10);
  const { query, inputs, variables } = getRehabSlotsWeeklyQueryFn(dateString);
  const myQuery = `
         query getRehabSlotsWeekly(${inputs}) {
              ${query}
         }
    `;
  try {
    const getRehabSlotsResponse = (await API.graphql({
      query: myQuery,
      variables: variables,
      authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
    })) as GraphQLResult<any>;

    const response = getRehabSlotsResponse.data.getRehabSlotsWeekly;
    if ("message" in response) {
      toast.error(response.message);
      throw new Error(response.message);
    }
    return response;
  } catch (error: any) {
    toast.error(error.errors[0].message);
    return false;
  }
};

export const changeRehabSlotBookability = async (
  datetime: string,
  bookable: boolean
) => {
  const { query, inputs, variables } = changeRehabSlotBookabilityQueryFn(
    datetime,
    bookable
  );

  const myQuery = `
        mutation changeRehabSlotBookability(${inputs}) {
            ${query}
        }
    `;

  try {
    const response = (await API.graphql({
      query: myQuery,
      variables: variables,
      authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
    })) as GraphQLResult<any>;

    const result = response.data.changeRehabSlotBookability;
    if ("message" in result) {
      toast.error(result.message);
      throw new Error(result.message);
    }
    return result;
  } catch (error: any) {
    toast.error(error.errors[0].message);
    return false;
  }
};

export const createRehabSlotComment = async (datetime:string, comment:string) => {
      const { query, inputs, variables } = createRehabSlotCommentQueryFn(datetime, comment);
  const myQuery = `
        mutation createRehabSlotComment(${inputs}) {
            ${query}
        }
    `;
  try { 
    const response = (await API.graphql({
      query: myQuery,
      variables: variables,
      authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
    })) as GraphQLResult<any>;

    const result = response.data.createRehabSlotComment;
    if ("message" in result) {
      toast.error(result.message);
      throw new Error(result.message);
    }
    return result;
  }
  catch (error: any) {
    toast.error(error.errors[0].message);
    return false;
  }
}
